import { resolveLobbyApiBase } from '../../config/serviceUrl';

const BROWSER_SESSION_KEY = 'npc:auth:browser_session_v1';
const AUTH_WINDOW_ID_KEY = 'npc:auth:window_id_v2';
const AUTH_WINDOW_SEQ_KEY = 'npc:auth:window_seq_v2';
const LOCAL_WINDOW_LEASE_PREFIX = 'npc:auth:window_lease_v2:';
const LEGACY_LIVE_WINDOWS_KEY = 'npc:auth:live_windows_v1';

const LOCAL_WINDOW_TTL_MS = Math.max(15_000, Number(process.env.REACT_APP_AUTH_WINDOW_LEASE_TTL_MS || 30_000));
const WINDOW_HEARTBEAT_MS = Math.max(3_000, Math.min(10_000, Number(process.env.REACT_APP_AUTH_WINDOW_HEARTBEAT_MS || 7_000)));
const LEASE_ENDPOINT = `${resolveLobbyApiBase()}/user/session/window-lease`;

let activeLeaseRuntime = null;

function readLocalStorage(key) {
  try { return localStorage.getItem(key); } catch (_) { return null; }
}

function writeLocalStorage(key, value) {
  try { localStorage.setItem(key, value); return true; } catch (_) { return false; }
}

function removeLocalStorage(key) {
  try { localStorage.removeItem(key); } catch (_) {}
}

function readSessionStorage(key) {
  try { return sessionStorage.getItem(key); } catch (_) { return null; }
}

function writeSessionStorage(key, value) {
  try { sessionStorage.setItem(key, value); return true; } catch (_) { return false; }
}

function removeSessionStorage(key) {
  try { sessionStorage.removeItem(key); } catch (_) {}
}

function parseJson(raw, fallback = null) {
  try { return JSON.parse(String(raw || '')); } catch (_) { return fallback; }
}

function readStoredUserCode() {
  try {
    const user = JSON.parse(readLocalStorage('user') || '{}') || {};
    return String(user?.user_code ?? user?.User_code ?? user?.userCode ?? '').trim();
  } catch (_) {
    return '';
  }
}

function readStoredSessionId() {
  return String(readLocalStorage('npc:auth:session_id') || '').trim();
}

function readStoredToken() {
  return String(
    readLocalStorage('token') ||
    readLocalStorage('accessToken') ||
    readLocalStorage('access_token') ||
    readLocalStorage('jwt') ||
    readLocalStorage('authToken') ||
    ''
  ).trim();
}

function makeWindowId() {
  try {
    if (globalThis?.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  } catch (_) {}
  return `w_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
}

// Different documents always get a different page instance. A popup that inherited
// sessionStorage from its opener is detected because the inherited window id is still
// actively claimed by another page_instance_id, and receives a new window id.
const PAGE_INSTANCE_ID = makeWindowId();

function localLeaseKey(windowId) {
  return `${LOCAL_WINDOW_LEASE_PREFIX}${String(windowId || '').trim()}`;
}

function getWindowId({ create = false, ensureUnique = false } = {}) {
  let id = String(readSessionStorage(AUTH_WINDOW_ID_KEY) || '').trim();
  if (!id && create) {
    id = makeWindowId();
    writeSessionStorage(AUTH_WINDOW_ID_KEY, id);
    writeSessionStorage(AUTH_WINDOW_SEQ_KEY, '0');
  }

  if (id && ensureUnique) {
    const existing = parseJson(readLocalStorage(localLeaseKey(id)), null);
    const ts = Number(existing?.ts || 0);
    const activeElsewhere =
      existing?.state === 'active' &&
      ts > 0 &&
      Date.now() - ts <= LOCAL_WINDOW_TTL_MS &&
      String(existing?.page_instance_id || '') &&
      String(existing.page_instance_id) !== PAGE_INSTANCE_ID;

    if (activeElsewhere) {
      id = makeWindowId();
      writeSessionStorage(AUTH_WINDOW_ID_KEY, id);
      writeSessionStorage(AUTH_WINDOW_SEQ_KEY, '0');
    }
  }

  return id;
}

function nextWindowSeq() {
  const current = Math.max(0, Math.floor(Number(readSessionStorage(AUTH_WINDOW_SEQ_KEY) || 0)));
  const next = current + 1;
  writeSessionStorage(AUTH_WINDOW_SEQ_KEY, String(next));
  return next;
}

function inferRouteKind() {
  try {
    const path = String(window.location?.pathname || '').toLowerCase();
    if (path === '/popup' || path.startsWith('/popup/')) return 'popup';
    if (path.startsWith('/table/')) return 'table';
    if (path === '/' || path.startsWith('/lobby')) return 'lobby';
    if (path.startsWith('/login')) return 'login';
  } catch (_) {}
  return 'app';
}

function writeLocalWindowLease(windowId, { state = 'active', seq = 0, routeKind = '' } = {}) {
  const id = String(windowId || '').trim();
  if (!id) return;
  const payload = {
    window_id: id,
    page_instance_id: PAGE_INSTANCE_ID,
    session_id: readStoredSessionId(),
    user_code: readStoredUserCode(),
    state: String(state || 'active'),
    seq: Number(seq || 0),
    route_kind: String(routeKind || inferRouteKind()),
    ts: Date.now(),
  };
  writeLocalStorage(localLeaseKey(id), JSON.stringify(payload));
}

function pruneLocalWindowLeases(now = Date.now()) {
  const currentSessionId = readStoredSessionId();
  const remove = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = String(localStorage.key(i) || '');
      if (!key.startsWith(LOCAL_WINDOW_LEASE_PREFIX)) continue;
      const row = parseJson(localStorage.getItem(key), null);
      const ts = Number(row?.ts || 0);
      const sid = String(row?.session_id || '').trim();
      if (!row || !ts || now - ts > LOCAL_WINDOW_TTL_MS * 3 || (currentSessionId && sid && sid !== currentSessionId && now - ts > LOCAL_WINDOW_TTL_MS)) {
        remove.push(key);
      }
    }
  } catch (_) {}
  remove.forEach(removeLocalStorage);
}

function postWindowLease(action, { windowId, routeKind = '', preferBeacon = false } = {}) {
  const token = readStoredToken();
  const id = String(windowId || getWindowId({ create: true, ensureUnique: true }) || '').trim();
  if (!token || !id) return false;

  const seq = nextWindowSeq();
  const normalizedAction = action === 'release' ? 'release' : 'heartbeat';
  const state = normalizedAction === 'release' ? 'released' : 'active';
  const kind = String(routeKind || inferRouteKind()).trim();
  writeLocalWindowLease(id, { state, seq, routeKind: kind });

  const params = new URLSearchParams();
  params.set('action', normalizedAction);
  params.set('token', token);
  params.set('window_id', id);
  params.set('seq', String(seq));
  params.set('auto_login', isAutoLoginEnabled() ? '1' : '0');
  params.set('route_kind', kind);

  if (preferBeacon) {
    try {
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        const accepted = navigator.sendBeacon(LEASE_ENDPOINT, params);
        if (accepted) return true;
      }
    } catch (_) {}
  }

  try {
    void fetch(LEASE_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
      body: params.toString(),
      keepalive: normalizedAction === 'release',
      cache: 'no-store',
      credentials: 'omit',
    }).catch(() => null);
    return true;
  } catch (_) {
    return false;
  }
}

function destroyLeaseRuntime({ removeLocal = false } = {}) {
  const runtime = activeLeaseRuntime;
  if (runtime) {
    try { window.clearInterval(runtime.timer); } catch (_) {}
    try { if (runtime.stopTimer) window.clearTimeout(runtime.stopTimer); } catch (_) {}
    try { window.removeEventListener('pagehide', runtime.onPageHide); } catch (_) {}
    try { window.removeEventListener('pageshow', runtime.onPageShow); } catch (_) {}
    try { document.removeEventListener('visibilitychange', runtime.onVisibilityChange); } catch (_) {}
    try { window.removeEventListener('online', runtime.onOnline); } catch (_) {}
    try { window.removeEventListener('focus', runtime.onFocus); } catch (_) {}
  }
  activeLeaseRuntime = null;

  if (removeLocal) {
    const id = getWindowId({ create: false });
    if (id) removeLocalStorage(localLeaseKey(id));
  }
}


function scheduleLeaseRuntimeStop(runtime) {
  if (!runtime || activeLeaseRuntime !== runtime || runtime.refs > 0) return;
  if (runtime.stopTimer) return;
  runtime.stopTimer = window.setTimeout(() => {
    runtime.stopTimer = null;
    if (activeLeaseRuntime !== runtime || runtime.refs > 0) return;
    destroyLeaseRuntime({ removeLocal: false });
  }, 500);
}

/**
 * Retry only infrastructure failures when /profile cannot yet be verified.
 * Keep the UI in CHECKING instead of becoming Guest (which would also erase
 * pending-match/table recovery hints). Definitive auth failures are returned
 * to the caller unchanged; successful replies are never fabricated.
 */
export async function restoreAuthWithTransientRetry(restore, { signal } = {}) {
  let failures = 0;
  while (!signal?.aborted) {
    const result = await restore();
    if (result?.ok || result?.reason !== 'server_error') return result;
    failures += 1;
    const retryMs = Math.min(20_000, Math.round(1_500 * Math.pow(2, Math.min(4, failures - 1))));
    await new Promise((resolve) => {
      let settled = false;
      let timer = null;
      const finish = () => {
        if (settled) return;
        settled = true;
        if (timer !== null) clearTimeout(timer);
        try { window.removeEventListener('online', finish); } catch (_) {}
        try { window.removeEventListener('focus', finish); } catch (_) {}
        try { window.removeEventListener('storage', onStorage); } catch (_) {}
        try { signal?.removeEventListener('abort', finish); } catch (_) {}
        resolve();
      };
      const onStorage = (event) => {
        if (!event?.key || ['token', 'npc:auth:session_id'].includes(event.key)) finish();
      };
      if (signal?.aborted) { resolve(); return; }
      timer = setTimeout(finish, retryMs);
      try { window.addEventListener('online', finish); } catch (_) {}
      try { window.addEventListener('focus', finish); } catch (_) {}
      try { window.addEventListener('storage', onStorage); } catch (_) {}
      try { signal?.addEventListener('abort', finish, { once: true }); } catch (_) {}
      if (signal?.aborted) finish();
    });
  }
  return { ok: false, reason: 'cancelled' };
}

export function hasStoredAuthToken() {
  return Boolean(readStoredToken());
}

export function isAutoLoginEnabled() {
  return readLocalStorage('auto_login') === '1';
}

export function hasBrowserAuthSession() {
  return Boolean(readSessionStorage(BROWSER_SESSION_KEY));
}

export function hasFreshLiveAuthWindow() {
  const now = Date.now();
  const currentId = getWindowId({ create: false });
  const currentSessionId = readStoredSessionId();
  pruneLocalWindowLeases(now);

  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = String(localStorage.key(i) || '');
      if (!key.startsWith(LOCAL_WINDOW_LEASE_PREFIX)) continue;
      const id = key.slice(LOCAL_WINDOW_LEASE_PREFIX.length);
      if (currentId && id === currentId) continue;
      const row = parseJson(localStorage.getItem(key), null);
      if (!row || row.state !== 'active') continue;
      if (currentSessionId && row.session_id && String(row.session_id) !== currentSessionId) continue;
      const ts = Number(row.ts || 0);
      if (ts > 0 && now - ts <= LOCAL_WINDOW_TTL_MS) return true;
    }
  } catch (_) {}
  return false;
}

export function markBrowserAuthSession({ autoLogin } = {}) {
  const payload = {
    user_code: readStoredUserCode(),
    session_id: readStoredSessionId(),
    started_at: Date.now(),
  };
  writeSessionStorage(BROWSER_SESSION_KEY, JSON.stringify(payload));

  if (autoLogin === true) writeLocalStorage('auto_login', '1');
  else if (autoLogin === false) removeLocalStorage('auto_login');

  // v1 shared JSON heartbeat is intentionally retired. Keeping one shared JSON object
  // allowed simultaneous windows to overwrite each other's entries.
  removeLocalStorage(LEGACY_LIVE_WINDOWS_KEY);
}

export function clearBrowserAuthSessionState() {
  const runtime = activeLeaseRuntime;
  if (runtime && hasStoredAuthToken()) {
    // Guest/logout transitions should stop counting this window immediately. The
    // request captures the token before local auth keys are removed by the caller.
    postWindowLease('release', {
      windowId: runtime.windowId,
      routeKind: runtime.routeKind,
      preferBeacon: false,
    });
  }
  destroyLeaseRuntime({ removeLocal: true });
  removeSessionStorage(BROWSER_SESSION_KEY);
  removeSessionStorage(AUTH_WINDOW_ID_KEY);
  removeSessionStorage(AUTH_WINDOW_SEQ_KEY);
  removeLocalStorage(LEGACY_LIVE_WINDOWS_KEY);
}

/**
 * Installs one lease owner per browser window/document.
 *
 * - F5 / full navigation: pagehide sends release(seq=N), the reloaded page reuses
 *   sessionStorage and sends heartbeat(seq=N+1). Redis ignores a late release N.
 * - Multiple windows: each window writes an independent localStorage key and an
 *   independent server lease, so there is no shared JSON read-modify-write race.
 * - Popup/table routes can call this directly; calls are reference-counted so lobby
 *   and route boundaries never create duplicate heartbeat loops in the same window.
 */
export function installAuthWindowLease(options = {}) {
  if (typeof window === 'undefined' || !hasStoredAuthToken()) return () => {};

  const requestedRouteKind = String(options?.routeKind || inferRouteKind()).trim() || 'app';

  if (activeLeaseRuntime) {
    const runtime = activeLeaseRuntime;
    runtime.refs += 1;
    runtime.routeKind = requestedRouteKind;
    if (runtime.stopTimer) {
      window.clearTimeout(runtime.stopTimer);
      runtime.stopTimer = null;
    }
    postWindowLease('heartbeat', {
      windowId: runtime.windowId,
      routeKind: runtime.routeKind,
    });

    let released = false;
    return () => {
      if (released || activeLeaseRuntime !== runtime) return;
      released = true;
      runtime.refs = Math.max(0, runtime.refs - 1);
      scheduleLeaseRuntimeStop(runtime);
    };
  }

  const windowId = getWindowId({ create: true, ensureUnique: true });
  pruneLocalWindowLeases();

  const runtime = {
    windowId,
    refs: 1,
    routeKind: requestedRouteKind,
    timer: null,
    stopTimer: null,
    onPageHide: null,
    onPageShow: null,
    onVisibilityChange: null,
    onOnline: null,
    onFocus: null,
  };
  activeLeaseRuntime = runtime;

  const heartbeat = () => {
    if (activeLeaseRuntime !== runtime || !hasStoredAuthToken()) return;
    postWindowLease('heartbeat', { windowId, routeKind: runtime.routeKind });
  };

  runtime.onPageHide = () => {
    if (activeLeaseRuntime !== runtime || !hasStoredAuthToken()) return;
    // Never delete the server session on pagehide. release is only a lease state
    // transition and is sequence-ordered against the next page heartbeat.
    postWindowLease('release', { windowId, routeKind: runtime.routeKind, preferBeacon: true });
  };

  runtime.onPageShow = () => {
    if (activeLeaseRuntime !== runtime || !hasStoredAuthToken()) return;
    heartbeat();
  };

  runtime.onVisibilityChange = () => {
    if (document.visibilityState === 'visible') heartbeat();
  };
  runtime.onOnline = heartbeat;
  runtime.onFocus = heartbeat;

  heartbeat();
  runtime.timer = window.setInterval(heartbeat, WINDOW_HEARTBEAT_MS);
  window.addEventListener('pagehide', runtime.onPageHide);
  window.addEventListener('pageshow', runtime.onPageShow);
  document.addEventListener('visibilitychange', runtime.onVisibilityChange);
  window.addEventListener('online', runtime.onOnline);
  window.addEventListener('focus', runtime.onFocus);

  let released = false;
  return () => {
    if (released || activeLeaseRuntime !== runtime) return;
    released = true;
    runtime.refs = Math.max(0, runtime.refs - 1);
    if (runtime.refs > 0) return;

    // React route transitions may unmount one owner immediately before mounting the
    // next one. Delay teardown briefly so lobby -> table transitions keep one lease.
    scheduleLeaseRuntimeStop(runtime);
  };
}

export function hasLocalTableRecoveryHint() {
  try {
    for (const key of ['currentTableCode', 'lastTableCode', 'tableCode']) {
      if (String(readLocalStorage(key) || '').trim()) return true;
    }

    const desktop = JSON.parse(readLocalStorage('desktop_multitable_v1') || 'null');
    const screens = Array.isArray(desktop?.screens) ? desktop.screens : [];
    if (screens.some((screen) => {
      const tableId = String(
        screen?.tableId ||
        (String(screen?.id || '').startsWith('table:') ? String(screen.id).slice('table:'.length) : '')
      ).trim();
      return Boolean(tableId);
    })) return true;
  } catch (_) {}

  try {
    const path = String(window.location?.pathname || '');
    const sp = new URLSearchParams(String(window.location?.search || ''));
    if (path.startsWith('/table/') || path.startsWith('/popup')) return true;
    if (String(sp.get('tableCode') || sp.get('table_code') || '').trim()) return true;
  } catch (_) {}

  return false;
}

export function isColdNonAutoLoginSession() {
  if (!hasStoredAuthToken()) return false;
  if (isAutoLoginEnabled()) return false;
  if (hasBrowserAuthSession()) return false;
  if (hasFreshLiveAuthWindow()) return false;
  return true;
}

export function shouldResumeStoredSessionFromLogin() {
  if (!hasStoredAuthToken()) return false;
  return (
    isAutoLoginEnabled() ||
    hasBrowserAuthSession() ||
    hasFreshLiveAuthWindow() ||
    hasLocalTableRecoveryHint()
  );
}
